import {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { message } from "antd";
import { INTENTS, loadCookiesData, getAuthWrapper, getReadyAuthUser } from "~/utils";
import i18n from "~/i18n";
import * as APITypes from "~/API";
import {
  apiClient,
  FinancePageLoaderResult,
  intentFailureFromApiResponse,
} from "~/services/apiClient";
import type {
  ExpiringLeasesResult,
  OutsideRentMonthLeasesResult,
  RentCollectionResult,
  StayCollectionResult,
} from "~/types/finance";
import { emptyExpiringLeasesResult } from "~/utils/finance/expiringLeases";
import { emptyOutsideRentMonthLeasesResult } from "~/utils/finance/outsideRentMonthLeases";
import { emptyStayCollectionResult } from "~/utils/finance/stayCollection";
import type { PropertyInfoReport } from "../../types/propertyInfoReport";
import {
  FINANCE_PAGE_TABS,
  parseFinancePageTab,
  type FinancePageTab,
} from "../../utils/financePageTabs";
import {
  financePeriodCanonicalRedirect,
  resolveFinancePeriodFromUrl,
} from "../../utils/financePeriodParams";

export type { PropertyInfoReport };
export { FINANCE_PAGE_TABS, type FinancePageTab };

export async function FinancePageLoader({
  request,
}: LoaderFunctionArgs): Promise<FinancePageLoaderType | Response> {
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user?.uid) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const requestUrl = new URL(request.url);
  const tab = parseFinancePageTab(requestUrl.searchParams.get("tab"));
  const { year: displayYear, month: displayMonth, needsCanonicalRedirect } =
    resolveFinancePeriodFromUrl(requestUrl);

  if (needsCanonicalRedirect) {
    return financePeriodCanonicalRedirect(requestUrl, displayYear, displayMonth);
  }

  const result = await apiClient.loader<FinancePageLoaderResult>(
    "finance-page",
    {
      ownerID,
      ownerType,
      userID: user.uid,
      year: String(displayYear),
      month: String(displayMonth),
    }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load finance page:", result.error);
    if (result.debugLogs?.length) {
      console.error(
        "[finance-page debugLogs]\n",
        result.debugLogs.map((line) => `  ${line}`).join("\n"),
      );
    }
    throw new Error(
      result.error ?? "Failed to load finance page",
    );
  }

  if (result.debugLogs?.length && process.env.NODE_ENV !== "production") {
    console.info(
      "[finance-page debugLogs]\n",
      result.debugLogs.map((line) => `  ${line}`).join("\n"),
    );
  }

  const data = result.data;
  const canCreateReport = isAdminOrSuperAdmin || data.canCreateReport;
  const canDeleteReport = isAdminOrSuperAdmin || data.canDeleteReport;

  return {
    financeStatusMonthlyList: data.financeStatusMonthlyList,
    propertyFinanceStatusMonthlyList: data.propertyFinanceStatusMonthlyList,
    financeStatusSummary: data.financeStatusSummary,
    currentYear: displayYear,
    currentMonth: displayMonth,
    canCreateReport,
    canDeleteReport,
    isAdminOrSuperAdmin,
    subscriptionPlan: data.subscriptionPlan,
    hasFinanceAccess: data.hasFinanceAccess,
    rentCollection: data.rentCollection ?? {
      neverPaid: [],
      inProgress: [],
      counts: { neverPaid: 0, inProgress: 0 },
    },
    stayCollection: data.stayCollection ?? emptyStayCollectionResult(),
    expiringLeases: data.expiringLeases ?? emptyExpiringLeasesResult(),
    outsideRentMonthLeases:
      data.outsideRentMonthLeases ?? emptyOutsideRentMonthLeasesResult(),
    tab,
    ...(result.debugLogs?.length ? { debugLogs: result.debugLogs } : {}),
  };
}

export type OverdueTenant = {
  tenant: APITypes.Tenant;
  overdue: number;
};

export type NextPayment = {
  tenant: APITypes.Tenant;
  when: string;
  amount: number;
};

export type ExpiringLease = {
  tenant: APITypes.Tenant;
  when: string;
  daysFromNow: number;
};

export type FinancePageLoaderType = {
  financeStatusSummary?: APITypes.YearlyStatusSummary;
  propertyFinanceStatusMonthlyList: PropertyInfoReport[];
  financeStatusMonthlyList: APITypes.MonthlyStatusReport[];
  currentYear: number;
  currentMonth: number;
  canCreateReport: boolean;
  canDeleteReport: boolean;
  isAdminOrSuperAdmin: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasFinanceAccess: boolean;
  rentCollection: RentCollectionResult;
  stayCollection: StayCollectionResult;
  expiringLeases: ExpiringLeasesResult;
  outsideRentMonthLeases: OutsideRentMonthLeasesResult;
  tab: FinancePageTab;
  /** Lambda diagnostic lines (successWithDebugLogs); present in non-prod / staging tests. */
  debugLogs?: string[];
};

const getNumericRequestValue = (
  formData: FormData,
  searchParams: URLSearchParams,
  key: "year" | "month",
): number => {
  const searchValue = searchParams.get(key);
  const parsedSearchValue =
    searchValue == null ? NaN : Number(searchValue);
  if (Number.isFinite(parsedSearchValue)) {
    return parsedSearchValue;
  }

  const formValue = formData.get(key);
  const parsedFormValue =
    typeof formValue === "string" ? Number(formValue) : NaN;
  if (Number.isFinite(parsedFormValue)) {
    return parsedFormValue;
  }

  return 0;
};

export async function FinancePageAction({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuthWrapper();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");
  const { searchParams } = new URL(request.url);

  const year = getNumericRequestValue(formData, searchParams, "year");
  const month = getNumericRequestValue(formData, searchParams, "month");

  const parseFormBody = (): Record<string, unknown> => {
    const raw = formData.get("body") as string | null;
    if (!raw) return {};
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  };

  switch (intent) {
    case INTENTS._refresh: {
      const err = "This feature is temporarily not available";
      message.error(err);
      return { success: false, error: err };
    }

    case INTENTS._generate_monthly_finance_report: {
      const result = await apiClient.sendIntent<APITypes.MonthlyStatusReport>(
        INTENTS._generate_monthly_finance_report,
        { year, month }
      );
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to generate monthly report",
        );
        message.error(failure.error);
        return failure;
      }
      message.success(i18n.t("Form_submitted_successfully"));
      return result.debugLogs?.length
        ? { ...result.data, debugLogs: result.debugLogs }
        : result.data;
    }

    case INTENTS._generate_yearly_finance_report: {
      const result = await apiClient.sendIntent<APITypes.YearlyStatusSummary>(
        INTENTS._generate_yearly_finance_report,
        { year }
      );
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to generate yearly report",
        );
        message.error(failure.error);
        return failure;
      }
      message.success(i18n.t("Form_submitted_successfully"));
      return result.data;
    }

    case INTENTS._delete_monthly_finance_report: {
      const payload = parseFormBody();
      const result = await apiClient.sendIntent<{ message: string }>(
        INTENTS._delete_monthly_finance_report,
        { year, month, ...payload }
      );
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to delete monthly report",
        );
        message.error(failure.error);
        return failure;
      }
      message.success(result.data.message);
      return { success: true, ...result.data };
    }

    case INTENTS._delete_yearly_finance_report: {
      const payload = parseFormBody();
      const result = await apiClient.sendIntent<{ message: string }>(
        INTENTS._delete_yearly_finance_report,
        { year, month, ...payload }
      );
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to delete yearly report",
        );
        message.error(failure.error);
        return failure;
      }
      message.success(result.data.message);
      return { success: true, ...result.data };
    }

    case INTENTS._bulk_request_rent_payments: {
      const payload = parseFormBody();
      const propertyID =
        (formData.get("propertyID") as string | null) ||
        (typeof payload.propertyID === "string" ? payload.propertyID : undefined);
      const result = await apiClient.sendIntent<{
        created: APITypes.Transaction[];
        skipped: Array<{
          leaseID: string;
          unitID: string;
          reason: string;
        }>;
        summary: {
          candidateCount: number;
          createdCount: number;
          skippedCount: number;
        };
      }>(INTENTS._bulk_request_rent_payments, {
        year,
        month,
        ...(propertyID ? { propertyID } : {}),
      });
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to create rent payment requests",
        );
        message.error(failure.error);
        return failure;
      }
      return result.data;
    }

    case INTENTS._bulk_request_stay_payments: {
      const payload = parseFormBody();
      const propertyID =
        (formData.get("propertyID") as string | null) ||
        (typeof payload.propertyID === "string" ? payload.propertyID : undefined);
      const result = await apiClient.sendIntent<{
        created: APITypes.Transaction[];
        skipped: Array<{
          stayID: string;
          unitID: string;
          reason: string;
        }>;
        summary: {
          candidateCount: number;
          createdCount: number;
          skippedCount: number;
        };
      }>(INTENTS._bulk_request_stay_payments, {
        year,
        month,
        ...(propertyID ? { propertyID } : {}),
      });
      if (!result.success || result.data == null) {
        const failure = intentFailureFromApiResponse(
          result,
          "Failed to create stay payment requests",
        );
        message.error(failure.error);
        return failure;
      }
      return result.data;
    }
  }
  return {};
}
